/**
 * Collapsible leads list picker with search and tag filter. Selected lead shown on top.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { ChevronDown, Filter, Search, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { LoadingSpinner, Input } from '@/components/ui';
import { NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import type { LeadsPickerSectionProps } from '../../drop-modals.types';
import { LeadCard } from './LeadCard';

type LeadWithTags = { performanceTags?: string[] };

export const LeadsPickerSection: FC<LeadsPickerSectionProps> = ({
  leadsLists,
  tagColors,
  selectedId,
  onSelect,
  search,
  onSearch,
  showLeads,
  onToggleShow,
  usageByListId,
  tagsLoading,
  tagFilterOptions: tagFilterOptionsProp,
}) => {
  const [leadTagFilter, setLeadTagFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const availableTags = useMemo(() => {
    if (tagFilterOptionsProp?.length) return tagFilterOptionsProp;
    const set = new Set<string>(['New', 'Unused']);
    for (const l of leadsLists) {
      for (const t of (l as LeadWithTags).performanceTags ?? [])
        if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [leadsLists, tagFilterOptionsProp]);

  const filtered = useMemo(() => {
    let list = leadsLists.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()));
    if (leadTagFilter) {
      if (leadTagFilter === '(none)') {
        list = list.filter((l) => !((l as LeadWithTags).performanceTags?.length));
      } else {
        const q = leadTagFilter.toLowerCase();
        list = list.filter((l) => (l as LeadWithTags).performanceTags?.some((t) => t.toLowerCase() === q));
      }
    }
    return [...list].sort((a, b) => {
      const aSel = a.id === selectedId;
      const bSel = b.id === selectedId;
      return aSel === bSel ? 0 : aSel ? -1 : 1;
    });
  }, [leadsLists, search, leadTagFilter, selectedId]);

  return (
    <section className="flex flex-1 flex-col space-y-2 overflow-hidden">
      <button
        type="button"
        onClick={onToggleShow}
        className="flex w-full shrink-0 items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          Leads list
        </h4>
        <ChevronDown className={cn('h-4 w-4 transition-transform', showLeads && 'rotate-180')} />
      </button>
      {showLeads && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-border bg-background p-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search leads lists..." className="h-9 pl-9" />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                {tagsLoading ? (
                  <span className="flex h-8 items-center gap-1.5 text-xs text-muted-foreground">
                    <LoadingSpinner size="xs" /> Tags…
                  </span>
                ) : (
                  <TagFilterDropdown
                    value={leadTagFilter === '(none)' ? NO_TAGS_VALUE : leadTagFilter}
                    options={availableTags}
                    tagColors={tagColors}
                    displayLabel={leadTagFilter === '' ? 'All tags' : leadTagFilter === '(none)' ? 'No tags' : leadTagFilter}
                    onSelect={(v) => setLeadTagFilter(v === '' ? '' : v === NO_TAGS_VALUE ? '(none)' : v)}
                    placeholder="All tags"
                    compact
                  />
                )}
              </div>
            </div>
          </div>
          <div ref={containerRef} className="leads-grid-container min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-muted/5 p-4">
            <div className="leads-grid w-full" style={{ overflowAnchor: 'none' }}>
              {filtered.length === 0 && (
                <div className="col-span-2 py-10 text-center text-sm text-muted-foreground">
                  {search || leadTagFilter ? 'No leads lists match your filters.' : 'No leads lists.'}
                </div>
              )}
              {filtered.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead as typeof lead & LeadWithTags}
                  isSelected={selectedId === lead.id}
                  tagColors={tagColors}
                  usageByListId={usageByListId}
                  onSelect={onSelect}
                  containerRef={containerRef}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
